import { defaultTo } from "lodash-es";
import { NFL } from "@/constants/sports";
import {
  GameStatus,
  Maybe,
  NflScheduleGame,
  SrNflGameStatusEnum,
} from "@/graphql/gql/graphql";
import { FixtureTeamSummary } from "@/types/scoreBoard";

type GameCardTeamSummary = {
  currentScore: number | null;
  fixtureMoneyLine: { odds: string; link: string };
  fixtureSpread: { odds: string; value: string; link: string };
  fixtureTotal: { odds: string; value: string; link: string };
  imgAlt: string;
  imgSrc: string;
  abbreviation: string;
  primaryColor: string;
  record: string;
  winProbability: number;
  sportsbookLink: string;
};

type WinProbabilities = {
  hWinProbability: number;
  aWinProbability: number;
};

export const getRecord = (
  winCount: number,
  lossCount: number,
  drawCount: number
) => {
  if (drawCount) {
    return `${winCount}-${lossCount}-${drawCount}`;
  }
  return `${winCount}-${lossCount}`;
};

export const formatOdds = (odds?: number | null) => {
  if (odds == null) {
    return "";
  }
  return odds > 0 ? `+${odds}` : `${odds}`;
};

export const formatHandicap = (handicap?: number | string | null) => {
  if (handicap == null) {
    return "";
  }
  return Number(handicap) > 0 ? `+${handicap}` : `${handicap}`;
};

export const parseWinProbability = (
  gameStatus: Maybe<GameStatus> | undefined,
  aWinProbability: number,
  hWinProbability: number
) => {
  const result: WinProbabilities = {
    aWinProbability: 0,
    hWinProbability: 0,
  };

  if (gameStatus !== GameStatus.Scheduled) {
    return result;
  }

  if (aWinProbability > hWinProbability) {
    result.aWinProbability = aWinProbability;
  } else {
    result.hWinProbability = hWinProbability;
  }

  return result;
};

export const parseTeamSummary = (teamArg: FixtureTeamSummary) => {
  const result: GameCardTeamSummary = {
    currentScore: null,
    fixtureMoneyLine: { odds: "", link: "" },
    fixtureSpread: { odds: "", value: "", link: "" },
    fixtureTotal: { odds: "", value: "", link: "" },
    imgAlt: "",
    imgSrc: "",
    abbreviation: "",
    primaryColor: "",
    record: "",
    winProbability: 0,
    sportsbookLink: "",
  };

  if (!teamArg) {
    return result;
  }

  const {
    currentScore,
    fixtureSpread,
    fixtureMoneyLine,
    fixtureTotal,
    winProbability,
    team: {
      name,
      image,
      abbreviation,
      primaryColor,
      winCount,
      loseCount,
      drawCount,
      sportsbookLink,
    },
  } = teamArg;

  result.currentScore = currentScore || null;

  if (fixtureMoneyLine != null) {
    const { link, odds } = fixtureMoneyLine;
    result.fixtureMoneyLine = { link: link ?? "", odds: formatOdds(odds) };
  }
  if (fixtureSpread != null) {
    const { link, value, odds } = fixtureSpread;
    result.fixtureSpread = {
      link: link ?? "",
      value: formatHandicap(value),
      odds: formatOdds(odds),
    };
  }
  if (fixtureTotal != null) {
    const { link, value, odds } = fixtureTotal;
    result.fixtureTotal = {
      link: link ?? "",
      value: value?.toString() ?? "",
      odds: formatOdds(odds),
    };
  }
  result.imgAlt = `${name || ""} logo`;
  result.imgSrc = image?.url || "";
  result.abbreviation = abbreviation || "";
  result.primaryColor = primaryColor || "";
  result.record = getRecord(winCount || 0, loseCount || 0, drawCount || 0);
  result.winProbability = winProbability ?? 0;
  result.sportsbookLink = sportsbookLink || "";

  return result;
};

type GetCurrentGameTimeArgs = Pick<NflScheduleGame, "quarter" | "clock">;
export const getCurrentGameTime = ({
  quarter,
  clock,
}: GetCurrentGameTimeArgs) => {
  if (clock?.toLowerCase() === "halftime") {
    return "Halftime";
  }
  if (clock?.toLowerCase() === "event over") {
    return "Event Over";
  }
  if (clock && quarter) {
    return `${quarter} - ${clock}`;
  }
  return clock;
};

export type GameStatusEnum = {
  [key in SrNflGameStatusEnum]: GameStatus;
};

const gameStatusMap: GameStatusEnum = {
  [SrNflGameStatusEnum.Scheduled]: GameStatus.Scheduled,
  [SrNflGameStatusEnum.Created]: GameStatus.Scheduled,
  [SrNflGameStatusEnum.InProgress]: GameStatus.InProgress,
  [SrNflGameStatusEnum.Halftime]: GameStatus.InProgress,
  [SrNflGameStatusEnum.Complete]: GameStatus.Final,
  [SrNflGameStatusEnum.Closed]: GameStatus.Final,
  [SrNflGameStatusEnum.Cancelled]: GameStatus.Scheduled,
  [SrNflGameStatusEnum.Postponed]: GameStatus.Scheduled,
  [SrNflGameStatusEnum.TimeTbd]: GameStatus.Scheduled,
  [SrNflGameStatusEnum.FlexSchedule]: GameStatus.Scheduled,
  [SrNflGameStatusEnum.Delayed]: GameStatus.InProgress,
  [SrNflGameStatusEnum.Suspended]: GameStatus.Scheduled,
};

type FindGameStateArgs = Pick<
  NflScheduleGame,
  "status" | "clock" | "scheduledTime"
>;
export const findGameState = ({
  status,
  clock,
  scheduledTime,
}: FindGameStateArgs) => {
  const gameState: GameStatus = status
    ? gameStatusMap[status]
    : GameStatus.Scheduled;

  if (gameState === GameStatus.Final) {
    return GameStatus.Final;
  }
  if (clock?.toLowerCase() === "event over") {
    return GameStatus.Final;
  }
  if (gameState === GameStatus.InProgress) {
    return GameStatus.InProgress;
  }
  if (!scheduledTime) {
    return gameState;
  }
  const gameTime = new Date(scheduledTime);
  const currentTime = new Date();
  const timeToGame = gameTime.getTime() - currentTime.getTime();
  if (gameState === GameStatus.Scheduled && clock === null && timeToGame > 0) {
    return GameStatus.Scheduled;
  }

  if (clock !== null || timeToGame < 0) {
    return GameStatus.InProgress;
  }
  return gameState;
};

export const normalizeNflFixture = (game: Maybe<NflScheduleGame>) => ({
  id: game?.id ?? "",
  scheduledStartTime: game?.scheduledTime,
  currentGameTime: getCurrentGameTime({
    quarter: game?.quarter,
    clock: game?.clock,
  }),
  gameStatus: game?.status ? findGameState(game) : GameStatus.Scheduled,
  eventUrl: null, // not needed for NFL fixtures, replaced by headToHeadSlug
  headToHeadSlug: game?.headToHeadSlug,
  tvNetwork: {
    name: game?.broadcastInfo?.network ?? "",
  },
  sport: {
    name: NFL,
  },
  teams: [
    {
      currentScore: game?.homeTeam?.stats?.points ?? null,
      winProbability: game?.homeTeam?.winProbability ?? null,
      fixtureMoneyLine: {
        odds: defaultTo(Number(game?.odds?.home?.moneyLine?.odd), null),
        link: game?.odds?.home?.moneyLine?.url ?? "",
      },
      fixtureSpread: {
        odds: defaultTo(Number(game?.odds?.home?.spread?.odd), null),
        value: defaultTo(Number(game?.odds?.home?.spread?.value), null),
        link: game?.odds?.home?.spread?.url ?? "",
      },
      fixtureTotal: {
        odds: defaultTo(Number(game?.odds?.home?.total?.odd), null),
        value: game?.odds?.home?.total?.value ?? null,
        link: game?.odds?.home?.total?.url ?? "",
      },
      team: {
        abbreviation: game?.homeTeam?.code,
        name: game?.homeTeam?.name ?? "",
        primaryColor: game?.homeTeam?.primaryColor,
        winCount: game?.homeTeam?.record?.wins ?? 0,
        loseCount: game?.homeTeam?.record?.losses ?? 0,
        drawCount: game?.homeTeam?.record?.ties ?? 0,
        sportsbookLink: game?.homeTeam?.sportsbookLink ?? "",
        image: {
          url: game?.homeTeam?.logo ?? "",
        },
      },
    },
    {
      currentScore: game?.awayTeam?.stats?.points ?? null,
      winProbability: game?.awayTeam?.winProbability ?? null,
      fixtureMoneyLine: {
        odds: defaultTo(Number(game?.odds?.away?.moneyLine?.odd), null),
        link: game?.odds?.away?.moneyLine?.url ?? "",
      },
      fixtureSpread: {
        odds: defaultTo(Number(game?.odds?.away?.spread?.odd), null),
        value: defaultTo(Number(game?.odds?.away?.spread?.value), null),
        link: game?.odds?.away?.spread?.url ?? "",
      },
      fixtureTotal: {
        odds: defaultTo(Number(game?.odds?.away?.total?.odd), null),
        value: game?.odds?.away?.total?.value ?? null,
        link: game?.odds?.away?.total?.url ?? "",
      },
      team: {
        abbreviation: game?.awayTeam?.code,
        name: game?.awayTeam?.name ?? "",
        primaryColor: game?.awayTeam?.primaryColor,
        winCount: game?.awayTeam?.record?.wins ?? 0,
        loseCount: game?.awayTeam?.record?.losses ?? 0,
        drawCount: game?.awayTeam?.record?.ties ?? 0,
        sportsbookLink: game?.awayTeam?.sportsbookLink ?? "",
        image: {
          url: game?.awayTeam?.logo ?? "",
        },
      },
    },
  ],
});
